import { Star } from 'lucide-react';

interface TestimonialProps {
  variant?: 'card' | 'compact';
  quote: string;
  name: string;
  credentials?: string;
  photoSrc: string;
  photoAlt: string;
  trustpilotUrl?: string;
  className?: string;
}

export function Testimonial({
  variant = 'card',
  quote,
  name,
  credentials,
  photoSrc,
  photoAlt,
  trustpilotUrl,
  className = '',
}: TestimonialProps) {
  if (variant === 'compact') {
    return (
      <figure
        className={`flex flex-col sm:flex-row items-start gap-4 sm:gap-5 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm rounded-xl border border-gray-200/70 dark:border-gray-700/60 shadow-sm p-5 sm:p-6 ${className}`}
      >
        <img
          src={photoSrc}
          alt={photoAlt}
          width={56}
          height={56}
          loading="lazy"
          className="w-14 h-14 rounded-full object-cover ring-2 ring-white dark:ring-gray-700 shadow-sm flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div
            className="flex gap-0.5 text-[#F37022] mb-2"
            aria-label="5 out of 5 stars"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-current" />
            ))}
          </div>
          <blockquote className="text-base text-gray-800 dark:text-gray-200 leading-relaxed mb-2">
            &ldquo;{quote}&rdquo;
          </blockquote>
          <figcaption className="flex items-center justify-between flex-wrap gap-x-3 gap-y-1">
            <div className="text-sm">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </span>
              {credentials && (
                <span className="text-gray-500 dark:text-gray-400">
                  {' '}· {credentials}
                </span>
              )}
            </div>
            {trustpilotUrl && (
              <a
                href={trustpilotUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-[#F37022] transition-colors min-h-[44px] py-2"
              >
                <Star className="w-3 h-3 fill-current text-[#00B67A]" />
                <span>Verified on Trustpilot</span>
              </a>
            )}
          </figcaption>
        </div>
      </figure>
    );
  }

  return (
    <figure
      className={`relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 md:p-10 overflow-hidden ${className}`}
    >
      <span
        aria-hidden="true"
        className="absolute top-2 left-4 text-7xl md:text-8xl leading-none text-[#F37022]/10 font-serif select-none pointer-events-none"
      >
        &ldquo;
      </span>

      <div
        className="flex gap-0.5 text-[#F37022] mb-4 relative z-10"
        aria-label="5 out of 5 stars"
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="w-4 h-4 fill-current" />
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-5 md:gap-6 items-start md:items-center relative z-10">
        <img
          src={photoSrc}
          alt={photoAlt}
          width={80}
          height={80}
          loading="lazy"
          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-4 ring-white dark:ring-gray-700 shadow-md flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <blockquote className="text-lg md:text-xl text-gray-800 dark:text-gray-200 leading-relaxed mb-4">
            &ldquo;{quote}&rdquo;
          </blockquote>
          <figcaption className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2">
            <div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {name}
              </div>
              {credentials && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {credentials}
                </div>
              )}
            </div>
            {trustpilotUrl && (
              <a
                href={trustpilotUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-[#F37022] transition-colors min-h-[44px] py-2"
              >
                <Star className="w-3.5 h-3.5 fill-current text-[#00B67A]" />
                <span>Verified on Trustpilot</span>
              </a>
            )}
          </figcaption>
        </div>
      </div>
    </figure>
  );
}

export const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/jdsimplified.com';
